import { Box, Theme, Tooltip, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { GRID_MS, snapEndMs, snapStartMs } from "./loadingEditPlanning";
import { createTimeScale, formatTick, timeAxisTicks } from "./timeScale";

dayjs.extend(utc);

export interface TimelineWindow {
  readonly id: string;
  readonly bucket: string;
  readonly startMs: number;
  readonly endMs: number;
  readonly heatId: string;
  readonly basketNumber: number;
  readonly pending?: boolean;
  readonly proposal?: boolean;
  readonly amended?: boolean;
  readonly idle?: boolean;
  readonly reviewMark?: "approved" | "declined";
}

// Idle edits assert no loading, so they carry no heat and paint as a void.
const IDLE_GLYPH = "∅";

export type DropCategory =
  "addition" | "removal" | "rearrangement" | "unassigned";

const ALWAYS_SOLID_DROP_CATEGORIES: ReadonlySet<DropCategory> = new Set([
  "removal",
  "unassigned",
]);

export interface TimelineDrop {
  readonly bucket: string;
  readonly ms: number;
  readonly category: DropCategory;
  readonly heatId: string;
  readonly toArea: string | null;
  readonly title: string;
}

export interface TimelineChargeEvent {
  readonly bucket: string;
  readonly ms: number;
  readonly heatId: string;
  readonly title: string;
}

const LANE_HEIGHT = 22;
const BUCKET_GAP = 10;
const AXIS_HEIGHT = 20;
const MIN_GUTTER_WIDTH = 130;
const LABEL_X = 8;
const LABEL_FONT_PX = 11;
// Labels render in the monospace face, so a fixed advance sizes them exactly.
const LABEL_CHAR_PX = 0.6 * LABEL_FONT_PX;
const LANE_DOT_R = 4;
const LANE_DOT_SPACE = 12;
const MIN_DOMAIN_MS = 60 * 1000;
const ROW_HEIGHT = LANE_HEIGHT + BUCKET_GAP;
const EDGE_PX = 6;
const DRAG_THRESHOLD_PX = 3;
const TITLE_FORMAT = "MMM D HH:mm:ss";

export const timelineGutterWidth = (buckets: readonly string[]): number =>
  Math.max(
    MIN_GUTTER_WIDTH,
    2 * LABEL_X +
      LANE_DOT_SPACE +
      Math.ceil(
        Math.max(0, ...buckets.map((bucket) => bucket.length)) * LABEL_CHAR_PX
      )
  );

export const timelineHeight = (buckets: readonly string[]): number =>
  AXIS_HEIGHT + buckets.length * ROW_HEIGHT;

// A stable hash of the heat id, used to colour ids that are not plain integers.
const heatHash = (heatId: string): number => {
  let hash = 0;
  for (const char of heatId) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return hash;
};

// Colour cycle for heats, keyed by id. Avoids warning.main and info.main so a
// heat is never mistaken for an amended or proposal window. Extend by adding
// entries here.
export const dataColorPalette = (theme: Theme): readonly string[] => [
  theme.palette.data.blue,
  theme.palette.data.orange,
  theme.palette.data.green,
  theme.palette.data.pink,
  theme.palette.data.lightblue,
  theme.palette.data.red,
  theme.palette.data.teal,
  theme.palette.data.violet,
  theme.palette.data.rose,
];

// Heat ids are usually integers, so index straight off the id: consecutive heats
// get distinct colours until the cycle wraps. Non-integer ids fall back to the
// hash so they still get a stable colour.
const colorIndexForHeat = (heatId: string, paletteLength: number): number => {
  const numeric = Number(heatId);
  const key = Number.isInteger(numeric) ? numeric : heatHash(heatId);
  return ((key % paletteLength) + paletteLength) % paletteLength;
};

// Trace colour keyed by lane order, so a lane keeps its colour regardless of
// which areas happen to have scale data.
export const laneColorFor = (
  theme: Theme,
  buckets: readonly string[],
  area: string
): string => {
  const palette = dataColorPalette(theme);
  const index = buckets.indexOf(area);
  const key = index === -1 ? heatHash(area) : index;
  return palette[key % palette.length] ?? theme.palette.primary.main;
};

// Shared heat colour so a heat's window, charge line and table swatch all agree.
export const heatColorFor = (theme: Theme, heatId: string): string => {
  const palette = dataColorPalette(theme);
  return (
    palette[colorIndexForHeat(heatId, palette.length)] ??
    theme.palette.primary.main
  );
};

export const windowTitle = (
  window: Pick<
    TimelineWindow,
    "bucket" | "heatId" | "basketNumber" | "startMs" | "endMs" | "idle"
  >
): string => {
  const span = `${dayjs.utc(window.startMs).format(TITLE_FORMAT)}–${dayjs
    .utc(window.endMs)
    .format(TITLE_FORMAT)} UTC`;
  const claim = window.idle
    ? "idle"
    : `${window.heatId} · basket ${window.basketNumber}`;
  return `${window.bucket} · ${claim} · ${span}`;
};

type Gesture =
  | { kind: "zoomselect"; anchorMs: number; originX: number }
  | {
      kind: "resize";
      windowId: string;
      bucket: string;
      heatId: string;
      basketNumber: number;
      fixedMs: number;
      edge: "start" | "end";
      originX: number;
    }
  | { kind: "create"; bucket: string; anchorMs: number; originX: number }
  | { kind: "select"; windowId: string; originX: number };

type Preview =
  | {
      kind: "resize";
      windowId: string;
      bucket: string;
      heatId: string;
      basketNumber: number;
      edge: "start" | "end";
      startMs: number;
      endMs: number;
    }
  | { kind: "create"; bucket: string; startMs: number; endMs: number }
  | { kind: "zoomselect"; startMs: number; endMs: number };

const clipVisual = (
  window: TimelineWindow,
  startMs: number,
  endMs: number
): TimelineWindow[] => {
  if (endMs <= window.startMs || startMs >= window.endMs) return [window];
  const pieces: TimelineWindow[] = [];
  if (window.startMs < startMs) pieces.push({ ...window, endMs: startMs });
  if (window.endMs > endMs)
    pieces.push({ ...window, id: `${window.id}#r`, startMs: endMs });
  return pieces;
};

export interface LoadingWindowTimelineProps {
  readonly buckets: readonly string[];
  readonly windows: readonly TimelineWindow[];
  readonly domainStartMs: number;
  readonly domainEndMs: number;
  readonly selectedId: string | null;
  readonly onSelectWindow: (id: string) => void;
  readonly onEditWindow?: (id: string) => void;
  readonly onResizeWindow: (id: string, startMs: number, endMs: number) => void;
  readonly onCreateWindow: (
    bucket: string,
    startMs: number,
    endMs: number
  ) => void;
  readonly onDomainChange: (domainStartMs: number, domainEndMs: number) => void;
  readonly drops?: readonly TimelineDrop[];
  readonly chargeEvents?: readonly TimelineChargeEvent[];
  readonly highlightedToAreas?: ReadonlySet<string>;
  readonly cursorMs?: number | null;
  readonly nonEditableBuckets?: ReadonlySet<string>;
  readonly laneDotsVisible?: boolean;
}

export const LoadingWindowTimeline = ({
  buckets,
  windows,
  domainStartMs,
  domainEndMs,
  selectedId,
  onSelectWindow,
  onEditWindow,
  onResizeWindow,
  onCreateWindow,
  onDomainChange,
  drops,
  chargeEvents,
  highlightedToAreas,
  cursorMs,
  nonEditableBuckets,
  laneDotsVisible,
}: LoadingWindowTimelineProps) => {
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [widthPx, setWidthPx] = useState(0);
  const clipId = useId();
  const idleHatchId = useId();

  useEffect(() => {
    const element = svgRef.current;
    if (element === null) return;
    const observer = new ResizeObserver((entries) => {
      setWidthPx(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  const height = timelineHeight(buckets);

  const bucketTop = useMemo(() => {
    const tops = new Map<string, number>();
    buckets.forEach((bucket, index) => {
      tops.set(bucket, AXIS_HEIGHT + index * ROW_HEIGHT);
    });
    return tops;
  }, [buckets]);

  const gutterWidth = useMemo(() => timelineGutterWidth(buckets), [buckets]);

  const scale = useMemo(
    () =>
      createTimeScale({
        domainStartMs,
        domainEndMs,
        width: Math.max(widthPx - gutterWidth, 1),
      }),
    [domainStartMs, domainEndMs, widthPx, gutterWidth]
  );

  const ticks = useMemo(() => timeAxisTicks(scale, 8), [scale]);

  const timeAtClientX = (clientX: number): number => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (rect === undefined) return domainStartMs;
    const px = clientX - rect.left - gutterWidth;
    const width = Math.max(rect.width - gutterWidth, 1);
    return domainStartMs + (px / width) * (domainEndMs - domainStartMs);
  };

  const beginGesture = (event: React.PointerEvent, gesture: Gesture) => {
    gestureRef.current = gesture;
    svgRef.current?.setPointerCapture(event.pointerId);
  };

  const handleMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const gesture = gestureRef.current;
    if (gesture === null) return;
    const pointerMs = timeAtClientX(event.clientX);

    if (gesture.kind === "zoomselect") {
      setPreview({
        kind: "zoomselect",
        startMs: Math.min(gesture.anchorMs, pointerMs),
        endMs: Math.max(gesture.anchorMs, pointerMs),
      });
    } else if (gesture.kind === "resize") {
      const startMs =
        gesture.edge === "start"
          ? Math.min(snapStartMs(pointerMs), gesture.fixedMs - GRID_MS)
          : gesture.fixedMs;
      const endMs =
        gesture.edge === "end"
          ? Math.max(snapEndMs(pointerMs), gesture.fixedMs + GRID_MS)
          : gesture.fixedMs;
      setPreview({
        kind: "resize",
        windowId: gesture.windowId,
        bucket: gesture.bucket,
        heatId: gesture.heatId,
        basketNumber: gesture.basketNumber,
        edge: gesture.edge,
        startMs,
        endMs,
      });
    } else if (gesture.kind === "create") {
      setPreview({
        kind: "create",
        bucket: gesture.bucket,
        startMs: snapStartMs(Math.min(gesture.anchorMs, pointerMs)),
        endMs: snapEndMs(Math.max(gesture.anchorMs, pointerMs)),
      });
    }
  };

  const handleUp = (event: React.PointerEvent<SVGSVGElement>) => {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    svgRef.current?.releasePointerCapture(event.pointerId);
    const committed = preview;
    setPreview(null);
    if (gesture === null) return;

    if (
      gesture.kind === "select" &&
      Math.abs(event.clientX - gesture.originX) <= DRAG_THRESHOLD_PX
    ) {
      onSelectWindow(gesture.windowId);
    } else if (committed?.kind === "resize") {
      onResizeWindow(committed.windowId, committed.startMs, committed.endMs);
    } else if (
      committed?.kind === "create" &&
      committed.endMs - committed.startMs >= GRID_MS
    ) {
      onCreateWindow(committed.bucket, committed.startMs, committed.endMs);
    } else if (
      committed?.kind === "zoomselect" &&
      committed.endMs - committed.startMs >= MIN_DOMAIN_MS
    ) {
      onDomainChange(committed.startMs, committed.endMs);
    }
  };

  const renderWindows = useMemo((): (TimelineWindow & {
    previewing?: boolean;
  })[] => {
    if (preview === null || preview.kind === "zoomselect") return [...windows];
    if (preview.kind === "resize") {
      return windows.flatMap((window) => {
        if (window.id === preview.windowId)
          return [
            {
              ...window,
              startMs: preview.startMs,
              endMs: preview.endMs,
              previewing: true,
            },
          ];
        if (window.bucket !== preview.bucket) return [window];
        return clipVisual(window, preview.startMs, preview.endMs);
      });
    }
    return [
      ...windows.flatMap((window) =>
        window.bucket === preview.bucket
          ? clipVisual(window, preview.startMs, preview.endMs)
          : [window]
      ),
      {
        id: "__create__",
        bucket: preview.bucket,
        startMs: preview.startMs,
        endMs: preview.endMs,
        heatId: "",
        basketNumber: 0,
        previewing: true,
      },
    ];
  }, [windows, preview]);

  // A dashed guide with the exact timestamp shown while dragging an edge, so the
  // snap point is legible before the drag is committed.
  const renderGuide = (ms: number, key: string, labelLeft: boolean) => {
    const gx = gutterWidth + scale.timeToX(ms);
    return (
      <g key={key} clipPath={`url(#${clipId})`} pointerEvents="none">
        <line
          x1={gx}
          x2={gx}
          y1={AXIS_HEIGHT}
          y2={height}
          stroke={theme.palette.text.primary}
          strokeWidth={1}
          strokeDasharray="3 2"
        />
        <text
          x={labelLeft ? gx - 3 : gx + 3}
          y={AXIS_HEIGHT + 11}
          textAnchor={labelLeft ? "end" : "start"}
          fill={theme.palette.text.primary}
          fontSize={10}
          fontWeight={theme.typography.fontWeightMedium}
          fontFamily={theme.typography.body2Mono.fontFamily}
        >
          {dayjs.utc(ms).format("HH:mm:ss")}
        </text>
      </g>
    );
  };

  // Memoised so the drop Tooltips don't re-render on every resize move or cursor tick.
  const dropElements = useMemo(
    () =>
      (drops ?? []).map((drop, index) => {
        const dropTop = bucketTop.get(drop.bucket);
        if (dropTop === undefined) return null;
        const dropX = gutterWidth + scale.timeToX(drop.ms);
        const dimmed =
          !ALWAYS_SOLID_DROP_CATEGORIES.has(drop.category) &&
          highlightedToAreas !== undefined &&
          highlightedToAreas.size > 0 &&
          !highlightedToAreas.has(drop.toArea ?? "");
        const fill =
          drop.category === "addition"
            ? theme.palette.success.main
            : drop.category === "removal"
              ? theme.palette.error.main
              : drop.category === "unassigned"
                ? theme.palette.warning.main
                : theme.palette.grey[600];
        return (
          <Tooltip
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={index}
            title={drop.title}
            arrow
            disableInteractive
            enterDelay={0}
          >
            <circle
              cx={dropX}
              cy={dropTop + LANE_HEIGHT + BUCKET_GAP / 2 - 2.5}
              r={2.5}
              fill={fill}
              fillOpacity={dimmed ? 0.2 : 0.9}
              clipPath={`url(#${clipId})`}
            />
          </Tooltip>
        );
      }),
    [drops, highlightedToAreas, scale, bucketTop, clipId, theme, gutterWidth]
  );

  const chargeElements = useMemo(
    () =>
      (chargeEvents ?? []).map((charge, index) => {
        const top = bucketTop.get(charge.bucket);
        if (top === undefined) return null;
        const x = gutterWidth + scale.timeToX(charge.ms);
        return (
          <Tooltip
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={index}
            title={charge.title}
            arrow
            placement="top"
            disableInteractive
            enterDelay={0}
          >
            <line
              x1={x}
              x2={x}
              y1={top - BUCKET_GAP / 2}
              y2={top + LANE_HEIGHT + BUCKET_GAP / 2}
              stroke={heatColorFor(theme, charge.heatId)}
              strokeWidth={2}
              strokeDasharray="3 2"
              clipPath={`url(#${clipId})`}
            />
          </Tooltip>
        );
      }),
    [chargeEvents, scale, bucketTop, clipId, gutterWidth, theme]
  );

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "grey.300",
        borderRadius: 1,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        style={{
          display: "block",
          touchAction: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
        }}
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={gutterWidth} y={0} width="100%" height={height} />
          </clipPath>
          <pattern
            id={idleHatchId}
            width={6}
            height={6}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width={6} height={6} fill={theme.palette.grey[100]} />
            <line
              x1={0}
              y1={0}
              x2={0}
              y2={6}
              stroke={theme.palette.grey[500]}
              strokeWidth={1.5}
            />
          </pattern>
        </defs>
        <rect
          x={gutterWidth}
          y={0}
          width="100%"
          height={AXIS_HEIGHT}
          fill="transparent"
          style={{ cursor: "col-resize" }}
          onPointerDown={(event) => {
            beginGesture(event, {
              kind: "zoomselect",
              anchorMs: timeAtClientX(event.clientX),
              originX: event.clientX,
            });
          }}
        />

        {buckets.map((bucket) => {
          const editable = !(nonEditableBuckets?.has(bucket) ?? false);
          return (
            <g key={bucket}>
              <rect
                x={0}
                y={(bucketTop.get(bucket) ?? 0) - BUCKET_GAP / 2}
                width="100%"
                height={1}
                fill={theme.palette.grey[300]}
                pointerEvents="none"
              />
              <rect
                x={gutterWidth}
                y={bucketTop.get(bucket) ?? 0}
                width="100%"
                height={LANE_HEIGHT}
                fill="transparent"
                style={editable ? { cursor: "crosshair" } : undefined}
                onPointerDown={
                  editable
                    ? (event) => {
                        beginGesture(event, {
                          kind: "create",
                          bucket,
                          anchorMs: timeAtClientX(event.clientX),
                          originX: event.clientX,
                        });
                      }
                    : undefined
                }
              />
            </g>
          );
        })}

        {ticks.map((tick) => (
          <g key={tick.ms} style={{ pointerEvents: "none" }}>
            <line
              x1={gutterWidth + tick.x}
              x2={gutterWidth + tick.x}
              y1={AXIS_HEIGHT}
              y2={height}
              stroke={theme.palette.grey[200]}
            />
            <text
              x={gutterWidth + tick.x + 3}
              y={AXIS_HEIGHT - 6}
              fill={theme.palette.text.secondary}
              fontSize={10}
              fontFamily={theme.typography.body2Mono.fontFamily}
            >
              {formatTick(tick.ms)}
            </text>
          </g>
        ))}

        {buckets.map((bucket) => (
          <g key={bucket} style={{ pointerEvents: "none" }}>
            {/* The dot's slot is always reserved, opacity only — toggling the
                trace strip must not shift the gutter and re-scale the lanes. */}
            <circle
              data-lane-dot
              cx={LABEL_X + LANE_DOT_R}
              cy={(bucketTop.get(bucket) ?? 0) + LANE_HEIGHT / 2}
              r={LANE_DOT_R}
              fill={laneColorFor(theme, buckets, bucket)}
              opacity={laneDotsVisible ? 1 : 0}
            />
            <text
              x={LABEL_X + LANE_DOT_SPACE}
              y={(bucketTop.get(bucket) ?? 0) + LANE_HEIGHT / 2 + 4}
              fill={theme.palette.text.primary}
              fontSize={LABEL_FONT_PX}
              fontFamily={theme.typography.body2Mono.fontFamily}
            >
              {bucket}
            </text>
          </g>
        ))}

        {renderWindows.map((window) => {
          const top = bucketTop.get(window.bucket);
          if (top === undefined) return null;
          const x = gutterWidth + scale.timeToX(window.startMs);
          const w = Math.max(
            scale.timeToX(window.endMs) - scale.timeToX(window.startMs),
            1
          );
          const isSelected = window.id === selectedId;
          const isPreview = window.previewing ?? false;
          const isAmended = window.amended ?? false;
          const isProposal = window.proposal ?? false;
          const mark = window.reviewMark;
          const declined = isProposal && !isSelected && mark === "declined";
          const unreviewed =
            isProposal && !isSelected && !isAmended && mark === undefined;
          const isIdle = window.idle ?? false;
          const idleStyled = isIdle && !declined && !isAmended;
          const fillColor = isAmended
            ? theme.palette.warning.main
            : declined
              ? alpha(theme.palette.grey[500], 0)
              : unreviewed
                ? theme.palette.grey[400]
                : heatColorFor(theme, window.heatId);
          const fill = idleStyled ? `url(#${idleHatchId})` : fillColor;
          return (
            <Tooltip
              key={window.id}
              title={isPreview ? "" : windowTitle(window)}
              arrow
              disableInteractive
              enterDelay={0}
            >
              <g clipPath={`url(#${clipId})`}>
                <rect
                  x={x}
                  y={top}
                  width={w}
                  height={LANE_HEIGHT}
                  rx={2}
                  fill={fill}
                  fillOpacity={isPreview ? 0.6 : unreviewed ? 0.45 : 0.9}
                  stroke={
                    isSelected || isPreview
                      ? theme.palette.text.primary
                      : window.proposal
                        ? theme.palette.info.main
                        : window.pending
                          ? theme.palette.warning.main
                          : theme.palette.background.paper
                  }
                  strokeWidth={
                    isSelected || isPreview
                      ? 2
                      : window.pending || window.proposal
                        ? 1.5
                        : 0.5
                  }
                  strokeDasharray={
                    isPreview ||
                    ((window.pending || window.proposal) && !isSelected)
                      ? "3 2"
                      : undefined
                  }
                  style={{ cursor: "pointer" }}
                  onPointerDown={(event) => {
                    if (isPreview) return;
                    beginGesture(event, {
                      kind: "select",
                      windowId: window.id,
                      originX: event.clientX,
                    });
                  }}
                  onDoubleClick={() => {
                    if (!isPreview) onEditWindow?.(window.id);
                  }}
                />
                {w > 44 ? (
                  <text
                    x={x + 4}
                    y={top + LANE_HEIGHT / 2 + 3}
                    fill={
                      declined || idleStyled
                        ? theme.palette.text.primary
                        : theme.palette.getContrastText(fillColor)
                    }
                    fontSize={10}
                    fontFamily={theme.typography.body2Mono.fontFamily}
                    pointerEvents="none"
                  >
                    {isIdle
                      ? IDLE_GLYPH
                      : `${window.heatId}·${window.basketNumber}`}
                  </text>
                ) : null}
                {isPreview || w <= EDGE_PX * 3 ? null : (
                  <>
                    <rect
                      x={x - EDGE_PX / 2}
                      y={top}
                      width={EDGE_PX}
                      height={LANE_HEIGHT}
                      fill="transparent"
                      style={{ cursor: "ew-resize" }}
                      onPointerDown={(event) => {
                        beginGesture(event, {
                          kind: "resize",
                          windowId: window.id,
                          bucket: window.bucket,
                          heatId: window.heatId,
                          basketNumber: window.basketNumber,
                          fixedMs: window.endMs,
                          edge: "start",
                          originX: event.clientX,
                        });
                      }}
                    />
                    <rect
                      x={x + w - EDGE_PX / 2}
                      y={top}
                      width={EDGE_PX}
                      height={LANE_HEIGHT}
                      fill="transparent"
                      style={{ cursor: "ew-resize" }}
                      onPointerDown={(event) => {
                        beginGesture(event, {
                          kind: "resize",
                          windowId: window.id,
                          bucket: window.bucket,
                          heatId: window.heatId,
                          basketNumber: window.basketNumber,
                          fixedMs: window.startMs,
                          edge: "end",
                          originX: event.clientX,
                        });
                      }}
                    />
                  </>
                )}
              </g>
            </Tooltip>
          );
        })}

        {dropElements}
        {chargeElements}

        {preview?.kind === "zoomselect" ? (
          <rect
            x={gutterWidth + scale.timeToX(preview.startMs)}
            y={AXIS_HEIGHT}
            width={Math.max(
              scale.timeToX(preview.endMs) - scale.timeToX(preview.startMs),
              1
            )}
            height={height - AXIS_HEIGHT}
            fill={theme.palette.primary.main}
            fillOpacity={0.12}
            stroke={theme.palette.primary.main}
            strokeWidth={1}
            pointerEvents="none"
            clipPath={`url(#${clipId})`}
          />
        ) : null}

        {preview?.kind === "resize"
          ? renderGuide(
              preview.edge === "start" ? preview.startMs : preview.endMs,
              "guide-resize",
              preview.edge === "end"
            )
          : null}
        {preview?.kind === "create" ? (
          <>
            {renderGuide(preview.startMs, "guide-create-start", true)}
            {renderGuide(preview.endMs, "guide-create-end", false)}
          </>
        ) : null}

        {cursorMs != null &&
        cursorMs >= domainStartMs &&
        cursorMs <= domainEndMs ? (
          <line
            x1={gutterWidth + scale.timeToX(cursorMs)}
            x2={gutterWidth + scale.timeToX(cursorMs)}
            y1={AXIS_HEIGHT}
            y2={height}
            stroke={theme.palette.error.main}
            strokeWidth={1.5}
            clipPath={`url(#${clipId})`}
            pointerEvents="none"
          />
        ) : null}
        <line
          x1={gutterWidth}
          x2={gutterWidth}
          y1={0}
          y2={height}
          stroke={theme.palette.grey[300]}
          pointerEvents="none"
        />
      </svg>
    </Box>
  );
};
